import { SubscriptionMailSummary } from './mail-summary.js';
import type { CustomerMailboxDto, CustomerMailForwarderDto } from '@cpm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MailCheckStatus, MailDeliverabilityDto } from '@cpm/shared';
import { Check, Copy, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api/client.js';
import { Modal } from './Modal.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorNote,
  Input,
  Label,
  Spinner,
  type BadgeTone,
} from './ui.js';

/**
 * Mail panels (G10/G13, ADR-0019/0024): per-subscription mail management
 * — domains, mailboxes with quotas, aliases and forwarders on the
 * Stalwart server — plus the deliverability surface: required DNS
 * records (auto-published into panel zones, copy-paste elsewhere) and
 * live validation against the Gmail/Yahoo bulk-sender rules. Mailbox
 * passwords are write-only: set here, pushed to Stalwart, never shown
 * again.
 */

export function checkTone(status: MailCheckStatus): BadgeTone {
  switch (status) {
    case 'pass':
      return 'green';
    case 'warn':
      return 'yellow';
    case 'fail':
      return 'red';
    default:
      return 'gray';
  }
}

// ── Forms ───────────────────────────────────────────────────────────

export function DomainForm({
  subscriptionId,
  onDone,
}: {
  subscriptionId: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [domain, setDomain] = useState('');
  const [catchAll, setCatchAll] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api.post(`/api/mail/subscriptions/${subscriptionId}/domains`, {
        domain,
        catchAllAddress: catchAll.trim() === '' ? null : catchAll.trim(),
      }),
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ['mail'] });
      void queryClient.invalidateQueries({ queryKey: ['portal', 'subscriptions'] });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mail'] });
      void queryClient.invalidateQueries({ queryKey: ['portal', 'subscriptions'] });
      onDone();
    },
  });
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div>
        <Label htmlFor="mail-domain">Domain</Label>
        <Input
          id="mail-domain"
          required
          placeholder="example.com"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="mail-catchall">Catch-all address (optional)</Label>
        <Input
          id="mail-catchall"
          type="email"
          placeholder="office@example.com"
          value={catchAll}
          onChange={(e) => setCatchAll(e.target.value)}
        />
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Receives mail sent to any address on the domain that doesn't exist.
        </p>
      </div>
      {save.error != null && <ErrorNote error={save.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          Add domain
        </Button>
      </div>
    </form>
  );
}

export function MailboxForm({
  domain,
  existing,
  onDone,
  quotaCeilingMb = null,
}: {
  quotaCeilingMb?: number | null;
  domain: { id: string; domain: string };
  existing?: CustomerMailboxDto;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [localPart, setLocalPart] = useState(existing?.localPart ?? '');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState(existing?.displayName ?? '');
  const [quotaMb, setQuotaMb] = useState<string>(
    existing
      ? existing.quotaMb == null
        ? ''
        : String(existing.quotaMb)
      : String(Math.min(1024, quotaCeilingMb ?? 1024)),
  );
  const save = useMutation({
    mutationFn: () => {
      const quota = quotaMb.trim() === '' ? null : Number(quotaMb);
      if (existing) {
        return api.patch(`/api/mail/mailboxes/${existing.id}`, {
          ...(password !== '' ? { password } : {}),
          displayName: displayName.trim() === '' ? null : displayName.trim(),
          quotaMb: quota,
        });
      }
      return api.post(`/api/mail/domains/${domain.id}/mailboxes`, {
        localPart,
        password,
        displayName: displayName.trim() === '' ? null : displayName.trim(),
        quotaMb: quota,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mail'] });
      void queryClient.invalidateQueries({ queryKey: ['portal', 'subscriptions'] });
      onDone();
    },
  });
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      {!existing && (
        <div>
          <Label htmlFor="mailbox-local">Address</Label>
          <div className="flex items-center gap-2">
            <Input
              id="mailbox-local"
              required
              placeholder="jane"
              value={localPart}
              onChange={(e) => setLocalPart(e.target.value)}
            />
            <span className="whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
              @{domain.domain}
            </span>
          </div>
        </div>
      )}
      <div>
        <Label htmlFor="mailbox-password">
          {existing ? 'New password (leave empty to keep)' : 'Password'}
        </Label>
        <Input
          id="mailbox-password"
          type="password"
          required={!existing}
          minLength={10}
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Stored only on the mail server — the panel cannot show it later.
        </p>
      </div>
      <div>
        <Label htmlFor="mailbox-name">Display name (optional)</Label>
        <Input
          id="mailbox-name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="mailbox-quota">Disk quota (MB)</Label>
        {quotaCeilingMb === null && (
          <label className="mb-2 flex gap-2 text-sm">
            <input
              type="checkbox"
              checked={quotaMb === ''}
              onChange={(e) => setQuotaMb(e.target.checked ? '' : '1024')}
            />
            Unlimited
          </label>
        )}
        {quotaCeilingMb !== null && <p className="text-xs">Plan ceiling: {quotaCeilingMb} MB</p>}
        <Input
          id="mailbox-quota"
          disabled={quotaMb === '' && quotaCeilingMb === null}
          required={quotaCeilingMb !== null}
          max={quotaCeilingMb ?? undefined}
          type="number"
          min={1}
          value={quotaMb}
          onChange={(e) => setQuotaMb(e.target.value)}
        />
      </div>
      {save.error != null && <ErrorNote error={save.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          {existing ? 'Save changes' : 'Create mailbox'}
        </Button>
      </div>
    </form>
  );
}

export function MailboxPasswordReset({
  mailbox,
  onDone,
}: {
  mailbox: CustomerMailboxDto;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const reset = useMutation({
    mutationFn: () => api.patch(`/api/mail/mailboxes/${mailbox.id}`, { password }),
    onSettled: () => setPassword(''),
    onSuccess: () => {
      setPassword('');
      void queryClient.invalidateQueries({ queryKey: ['mail'] });
      onDone();
    },
  });
  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        reset.mutate();
      }}
    >
      <p className="text-sm">
        Changing this password will require mail apps and webmail to use the new password. The
        current password cannot be shown.
      </p>
      <div>
        <Label htmlFor="reset-mailbox-password">New mailbox password</Label>
        <Input
          id="reset-mailbox-password"
          type="password"
          autoComplete="new-password"
          minLength={10}
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </div>
      <label className="flex gap-2 text-sm">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
        />
        I will share the new password privately with the mailbox owner.
      </label>
      {reset.error && <ErrorNote error={reset.error} />}
      <Button type="submit" disabled={!confirmed} loading={reset.isPending}>
        Reset mailbox password
      </Button>
    </form>
  );
}

export function ForwarderForm({
  domain,
  existing,
  onDone,
}: {
  domain: { id: string; domain: string };
  existing?: CustomerMailForwarderDto;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [localPart, setLocalPart] = useState(existing?.localPart ?? '');
  const [recipients, setRecipients] = useState((existing?.recipients ?? []).join('\n'));
  const save = useMutation({
    mutationFn: () => {
      const list = recipients
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      return existing
        ? api.patch(`/api/mail/forwarders/${existing.id}`, { recipients: list })
        : api.post(`/api/mail/domains/${domain.id}/forwarders`, { localPart, recipients: list });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mail'] });
      void queryClient.invalidateQueries({ queryKey: ['portal', 'subscriptions'] });
      onDone();
    },
  });
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      {!existing && (
        <div>
          <Label htmlFor="fwd-local">Address</Label>
          <div className="flex items-center gap-2">
            <Input
              id="fwd-local"
              required
              placeholder="contact"
              value={localPart}
              onChange={(e) => setLocalPart(e.target.value)}
            />
            <span className="whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
              @{domain.domain}
            </span>
          </div>
        </div>
      )}
      <div>
        <Label htmlFor="fwd-recipients">Forward to (one address per line)</Label>
        <textarea
          id="fwd-recipients"
          required
          rows={4}
          className="block w-full rounded-lg border-0 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-brand-600 dark:bg-slate-800 dark:text-white dark:ring-slate-600"
          placeholder={'someone@gmail.com\nother@example.org'}
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
        />
      </div>
      {save.error != null && <ErrorNote error={save.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          {existing ? 'Save changes' : 'Create forwarder'}
        </Button>
      </div>
    </form>
  );
}

export function AliasForm({
  mailbox,
  onDone,
}: {
  mailbox: CustomerMailboxDto;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [localPart, setLocalPart] = useState('');
  const save = useMutation({
    mutationFn: () => api.post(`/api/mail/mailboxes/${mailbox.id}/aliases`, { localPart }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mail'] });
      void queryClient.invalidateQueries({ queryKey: ['portal', 'subscriptions'] });
      onDone();
    },
  });
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div>
        <Label htmlFor="alias-local">Alias address</Label>
        <div className="flex items-center gap-2">
          <Input
            id="alias-local"
            required
            placeholder="sales"
            value={localPart}
            onChange={(e) => setLocalPart(e.target.value)}
          />
          <span className="whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
            @{mailbox.address.split('@')[1]}
          </span>
        </div>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Delivers into {mailbox.address}.
        </p>
      </div>
      {save.error != null && <ErrorNote error={save.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          Add alias
        </Button>
      </div>
    </form>
  );
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      aria-label={label}
      className="shrink-0 rounded p-1 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? <Check className="size-3.5 text-green-600" /> : <Copy className="size-3.5" />}
    </button>
  );
}

export function DeliverabilityModal({
  domain,
  isAdmin,
  open,
  onClose,
}: {
  domain: { id: string; domain: string };
  isAdmin: boolean;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['mail', 'deliverability', domain.id],
    queryFn: () => api.get<MailDeliverabilityDto>(`/api/mail/domains/${domain.id}/deliverability`),
    enabled: open,
  });
  const check = useMutation({
    mutationFn: () =>
      api.post<MailDeliverabilityDto>(`/api/mail/domains/${domain.id}/deliverability/check`, {}),
    onSuccess: (data) => {
      queryClient.setQueryData(['mail', 'deliverability', domain.id], data);
      void queryClient.invalidateQueries({ queryKey: ['mail', 'subscription'] });
    },
  });
  const rotate = useMutation({
    mutationFn: () =>
      api.post<MailDeliverabilityDto>(`/api/mail/domains/${domain.id}/dkim/rotate`, {}),
    onSuccess: (data) => {
      queryClient.setQueryData(['mail', 'deliverability', domain.id], data);
    },
  });

  const data = query.data;
  return (
    <Modal title={`Deliverability — ${domain.domain}`} open={open} onClose={onClose} wide>
      {query.isPending ? (
        <Spinner />
      ) : query.error ? (
        <ErrorNote error={query.error} />
      ) : (
        <div className="space-y-5">
          {data!.liveError && (
            <ErrorNote error={new Error(`Showing cached records: ${data!.liveError}`)} />
          )}
          <div className="flex flex-wrap items-center justify-between gap-2">
            {data!.zone ? (
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Records are <strong>published automatically</strong> into the panel zone{' '}
                <span className="font-mono">{data!.zone.name}</span>. The list below is what the
                panel maintains.
              </p>
            ) : (
              <p className="text-sm text-slate-600 dark:text-slate-300">
                No panel DNS zone hosts this domain —{' '}
                <strong>publish these records at the domain's DNS provider</strong>, then validate.
              </p>
            )}
            <div className="flex items-center gap-1">
              {isAdmin && (
                <Button
                  variant="secondary"
                  loading={rotate.isPending}
                  onClick={() => {
                    if (
                      window.confirm(
                        'Request DKIM key rotation? New keys are generated by the mail server; keep the old records published until the new selectors appear and verify.',
                      )
                    ) {
                      rotate.mutate();
                    }
                  }}
                >
                  Rotate DKIM
                </Button>
              )}
              <Button loading={check.isPending} onClick={() => check.mutate()}>
                <RefreshCw className="size-4" /> Check now
              </Button>
            </div>
          </div>
          {(check.error ?? rotate.error) != null && (
            <ErrorNote error={check.error ?? rotate.error} />
          )}

          <div>
            <h4 className="mb-2 text-sm font-semibold">Required DNS records</h4>
            <div className="max-h-72 overflow-auto rounded-lg border border-slate-200 dark:border-slate-800">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
                  <tr>
                    <th className="px-3 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium">Type</th>
                    <th className="px-3 py-2 font-medium">Value</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {data!.records.map((record, i) => (
                    <tr key={i} className="align-top">
                      <td className="max-w-48 break-all px-3 py-2 font-mono">{record.name}</td>
                      <td className="px-3 py-2 font-mono">
                        {record.type}
                        {!record.panelSupported && (
                          <span
                            className="ml-1 text-slate-400"
                            title="Not auto-published (DANE) — manage manually"
                          >
                            *
                          </span>
                        )}
                      </td>
                      <td className="max-w-md break-all px-3 py-2 font-mono">{record.value}</td>
                      <td className="px-3 py-2">
                        <CopyButton
                          text={record.value}
                          label={`Copy ${record.type} value for ${record.name}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-sm font-semibold">Validation</h4>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {data!.summary.checkedAt
                  ? `Last checked ${new Date(data!.summary.checkedAt).toLocaleString()}`
                  : 'Never checked'}
              </span>
            </div>
            {data!.checks.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Run a check to validate the published records — Gmail and Yahoo require aligned SPF
                + DKIM, a DMARC record and forward-confirmed reverse DNS.
              </p>
            ) : (
              <ul className="space-y-2">
                {data!.checks.map((c) => (
                  <li
                    key={c.key}
                    className="rounded-lg border border-slate-200 p-3 dark:border-slate-800"
                  >
                    <p className="flex items-center gap-2 text-sm">
                      <Badge tone={checkTone(c.status)}>{c.status}</Badge>
                      <span className="font-medium">{c.label}</span>
                    </p>
                    <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">{c.detail}</p>
                    {c.observed && c.observed.length > 0 && (
                      <p className="mt-1 break-all font-mono text-xs text-slate-400">
                        observed: {c.observed.join(' · ')}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Subscription summaries ───────────────────────────────────────────

export function SubscriptionMailPanel({ subscriptionId }: { subscriptionId: string }) {
  return <SubscriptionMailSummary subscriptionId={subscriptionId} />;
}
export function CustomerMailPanel({ subscriptionId }: { subscriptionId: string }) {
  return <SubscriptionMailSummary subscriptionId={subscriptionId} portal />;
}
